/**
 * The feature registry.  ** FROZEN — WRITTEN ONCE, NEVER EDITED AGAIN **
 *
 * ============================================================================
 *  DO NOT EDIT THIS FILE TO ADD YOUR FEATURE.
 *
 *  Create `apps/web/src/features/<your-slot-id>/register.ts` with a default
 *  export of `FeatureModule` and you are registered. Nothing else. The slot ids
 *  below are already declared; discovery is `import.meta.glob`, so adding a
 *  directory is the whole installation step and two agents can land two
 *  features in the same wave without touching one shared file (plan §5.2 /
 *  §A8 — the anti-collision rule that also produced the frozen topic barrel).
 *
 *      // apps/web/src/features/viewport/register.ts
 *      import { Viewport } from '@tenmol/viewport';
 *      import type { FeatureModule } from '../registry';
 *      export default { id: 'viewport', Panel: Viewport } satisfies FeatureModule;
 *
 *  A slot with no directory renders its `absent` note instead of a panel. That
 *  is deliberate: an unbuilt feature must be VISIBLY unbuilt, never a blank
 *  space that looks like it works.
 * ============================================================================
 *
 * The ids are the `apps/web/src/features/<name>/**` directories assigned in plan
 * §6, plus their owning work package. Regions map onto the shell's CSS grid:
 *
 *   menubar        the top strip
 *   viewport       the GL surface and anything drawn over it
 *   internal-gui   PyMOL's right-hand column — the blocks `OrthoLayoutPanel()`
 *                  stacks bottom-up (`packages/engine/layer1/Ortho.cpp:2261-2340`): Executive,
 *                  Wizard, ButMode, Control
 *   external-gui   the bottom dock (feedback + command line + quick buttons)
 *   overlay        dialogs and floating panels
 *   service        no UI of its own; installs behaviour (keys, picking, menus)
 */

import type { ComponentType } from 'react';

/** Where a feature mounts: one of the shell's six layout regions. */
export type FeatureRegion =
  'menubar' | 'viewport' | 'internal-gui' | 'external-gui' | 'overlay' | 'service';

/** A declared feature directory: its region, owner, order and absent-note. */
export interface FeatureSlot {
  id: string;
  /** Human name, shown in the "not installed" placeholder and in diagnostics. */
  title: string;
  region: FeatureRegion;
  /** Work package that owns `features/<id>/**`. */
  owner: string;
  /** Sort key within the region. */
  order: number;
  /** What the region shows while the directory does not exist. */
  absent: string;
}

/** Every planned feature directory. 25 slots; four are live in this wave. */
export const FEATURE_SLOTS: readonly FeatureSlot[] = [
  // --- menubar -----------------------------------------------------------
  {
    id: 'menubar',
    title: 'Menu bar',
    region: 'menubar',
    owner: 'WP-14',
    order: 10,
    absent: 'menu bar not installed',
  },

  // --- viewport ----------------------------------------------------------
  {
    id: 'viewport',
    title: 'Viewport',
    region: 'viewport',
    owner: 'WP-09',
    order: 10,
    absent:
      'viewport not installed — add apps/web/src/features/viewport/register.ts exporting <Viewport/> from @tenmol/viewport',
  },
  {
    id: 'seqview',
    title: 'Sequence viewer',
    region: 'viewport',
    owner: 'WP-21',
    order: 20,
    absent: '',
  },
  {
    id: 'render',
    title: 'Ray / render',
    region: 'viewport',
    owner: 'WP-19',
    order: 30,
    absent: '',
  },

  // --- internal GUI column ----------------------------------------------
  {
    id: 'objects',
    title: 'Object panel',
    region: 'internal-gui',
    owner: 'WP-12',
    order: 10,
    absent: 'object panel not installed',
  },
  { id: 'wizards', title: 'Wizard', region: 'internal-gui', owner: 'WP-16', order: 20, absent: '' },
  {
    id: 'movie',
    title: 'Movie controls',
    region: 'internal-gui',
    owner: 'WP-20',
    order: 30,
    absent: '',
  },
  { id: 'scenes', title: 'Scenes', region: 'internal-gui', owner: 'WP-20', order: 40, absent: '' },

  // --- external GUI ------------------------------------------------------
  {
    id: 'console',
    title: 'Console',
    region: 'external-gui',
    owner: 'WP-11',
    order: 10,
    absent: 'console not installed',
  },

  // --- overlays ----------------------------------------------------------
  { id: 'settings', title: 'Settings', region: 'overlay', owner: 'WP-15', order: 10, absent: '' },
  { id: 'files', title: 'File dialogs', region: 'overlay', owner: 'WP-18', order: 20, absent: '' },
  { id: 'dialogs', title: 'Dialogs', region: 'overlay', owner: 'WP-18', order: 30, absent: '' },
  { id: 'builder', title: 'Builder', region: 'overlay', owner: 'WP-17', order: 40, absent: '' },
  { id: 'colors', title: 'Colours', region: 'overlay', owner: 'WP-22', order: 50, absent: '' },
  { id: 'volume', title: 'Volume', region: 'overlay', owner: 'WP-22', order: 60, absent: '' },
  {
    id: 'properties',
    title: 'Properties',
    region: 'overlay',
    owner: 'WP-22',
    order: 70,
    absent: '',
  },
  {
    id: 'texteditor',
    title: 'Text editor',
    region: 'overlay',
    owner: 'WP-22',
    order: 80,
    absent: '',
  },
  { id: 'compute', title: 'Compute', region: 'overlay', owner: 'WP-24', order: 90, absent: '' },
  { id: 'plugins', title: 'Plugins', region: 'overlay', owner: 'WP-25', order: 100, absent: '' },
  {
    id: 'plugin-manager',
    title: 'Plugin manager (read-only in v1)',
    region: 'overlay',
    owner: 'WP-25b',
    order: 110,
    absent: '',
  },
  { id: 'apbs', title: 'APBS', region: 'overlay', owner: 'WP-25a', order: 120, absent: '' },

  // --- services (no region of their own) ---------------------------------
  { id: 'picking', title: 'Picking', region: 'service', owner: 'WP-10', order: 10, absent: '' },
  {
    id: 'pymol-menu',
    title: 'PyMOL popup menus',
    region: 'service',
    owner: 'WP-13',
    order: 20,
    absent: '',
  },
  { id: 'keyboard', title: 'Keyboard', region: 'service', owner: 'WP-23', order: 30, absent: '' },
  { id: 'shortcuts', title: 'Shortcuts', region: 'service', owner: 'WP-23', order: 40, absent: '' },
];

/** The id of any declared slot. */
export type FeatureId = (typeof FEATURE_SLOTS)[number]['id'];

/** What `features/<id>/register.ts` must default-export. */
export interface FeatureModule {
  /** Must equal the directory name. Checked at load time. */
  id: string;
  /** The panel rendered in the slot's region. */
  Panel: ComponentType;
  /**
   * Optional: run once when the feature loads, for `service` features that
   * install behaviour instead of rendering. Returns a teardown.
   */
  install?: () => void | (() => void);
}

/* ------------------------------------------------------------------ *
 * Discovery
 * ------------------------------------------------------------------ */

/**
 * Lazy on purpose. An eager glob would make one feature's broken import a blank
 * application; lazily, a failure is confined to its own slot and reported there
 * by the shell's error boundary.
 */
const LOADERS = import.meta.glob('./*/register.ts') as Record<
  string,
  () => Promise<{ default: FeatureModule }>
>;

const BY_ID = new Map<string, () => Promise<{ default: FeatureModule }>>(
  Object.entries(LOADERS).map(([path, loader]) => [path.split('/')[1] ?? path, loader]),
);

const SLOT_BY_ID = new Map(FEATURE_SLOTS.map((slot) => [slot.id, slot]));

/** Directories present that no slot declares — a typo, and it must not be silent. */
export const UNDECLARED_FEATURES: readonly string[] = [...BY_ID.keys()].filter(
  (id) => !SLOT_BY_ID.has(id),
);

/** The slot declared for `id`, or undefined if none is. */
export function getSlot(id: string): FeatureSlot | undefined {
  return SLOT_BY_ID.get(id);
}

/** True when a `register.ts` for `id` exists on disk. */
export function isInstalled(id: string): boolean {
  return BY_ID.has(id);
}

/** The lazy import loader for `id`'s feature module, if installed. */
export function loadFeature(id: string): (() => Promise<{ default: FeatureModule }>) | undefined {
  return BY_ID.get(id);
}

/** All installed feature ids, sorted. */
export function installedFeatureIds(): readonly string[] {
  return [...BY_ID.keys()].sort();
}

/** The slots in `region`, in display order. */
export function slotsForRegion(region: FeatureRegion): readonly FeatureSlot[] {
  return FEATURE_SLOTS.filter((slot) => slot.region === region).sort((a, b) => a.order - b.order);
}
